// ─────────────────────────────────────────────────────────────────────────────
// Selector de personas para la asignación masiva de roles (minuta 2026-10-20).
//
// Las dos formas que se platicaron, juntas:
//   · como en Reconocer: se busca a una persona, se agrega a la lista y el campo queda libre para
//     «agregar otro», tantas veces como haga falta;
//   · como en el correo: se pega una lista de correos separados por punto y coma o coma y se
//     agregan todos los que existan en el directorio.
// Las personas con rol Admin General nunca entran a la lista: ese rol no se gestiona desde aquí.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar, UserSearchSelect } from '@/components/common';
import type { UserSearchSelection } from '@/components/common';
import type { AdminUserDto } from '@/api/recognition';
import { resolverCorreos } from './asignacionMasiva';

interface AdminRolePeoplePickerProps {
  people: UserSearchSelection[];
  onChange: (people: UserSearchSelection[]) => void;
  existingAdmins: AdminUserDto[];
  isDisabled?: boolean;
}

export function AdminRolePeoplePicker({
  people, onChange, existingAdmins, isDisabled = false,
}: Readonly<AdminRolePeoplePickerProps>) {
  const { t } = useTranslation('recognition');
  const [omitidosGlobal, setOmitidosGlobal] = useState<string[]>([]);
  const [noEncontrados, setNoEncontrados] = useState<string[]>([]);
  const [resolviendo, setResolviendo] = useState(0);

  const rolDe = (id: string) => existingAdmins.find((admin) => admin.id === id)?.role;

  /** Agrega sin repetir y deja fuera a los Admin General, avisando quiénes fueron. */
  function agregar(nuevas: UserSearchSelection[]) {
    const yaEstan = new Set(people.map((persona) => persona.id));
    const globales = nuevas.filter((persona) => rolDe(persona.id) === 'AdminGlobal');
    const validas = nuevas.filter((persona) => rolDe(persona.id) !== 'AdminGlobal' && !yaEstan.has(persona.id));
    setOmitidosGlobal(globales.map((persona) => persona.displayName || persona.email || persona.id));
    if (validas.length > 0) onChange([...people, ...validas]);
  }

  async function agregarCorreos(correos: string[]) {
    setNoEncontrados([]);
    setResolviendo(correos.length);
    try {
      const resultado = await resolverCorreos(correos);
      agregar(resultado.encontrados);
      setNoEncontrados(resultado.noEncontrados);
    } finally {
      setResolviendo(0);
    }
  }

  const quitar = (id: string) => onChange(people.filter((persona) => persona.id !== id));

  return (
    <div className="adm-field">
      <span>{t('admin.roles.peopleLabel')}</span>
      <p className="adm-role-note">{t('admin.roles.peopleHint')}</p>
      {!isDisabled && (
        <UserSearchSelect
          inputId="adm-add-admin-search"
          value={null}
          onSelect={(seleccion) => { if (seleccion) agregar([seleccion]); }}
          placeholder={t('admin.roles.searchPersonPlaceholder')}
          excludeIds={people.map((persona) => persona.id)}
          isClearedOnSelect
          onPasteList={(correos) => { void agregarCorreos(correos); }}
        />
      )}
      {resolviendo > 0 && (
        <p className="adm-role-note" role="status">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('admin.roles.resolvingEmails', { count: resolviendo })}
        </p>
      )}
      {omitidosGlobal.length > 0 && (
        <p className="adm-role-note" role="alert">{t('admin.roles.globalSkipped', { names: omitidosGlobal.join(', ') })}</p>
      )}
      {noEncontrados.length > 0 && (
        <p className="adm-role-note adm-role-note--warn" role="alert">
          {t('admin.roles.emailsNotFound', { emails: noEncontrados.join(', ') })}
        </p>
      )}

      {people.length > 0 && (
        <>
          <div className="adm-people-head">
            <span>{t('admin.roles.peopleCount', { count: people.length })}</span>
            {!isDisabled && people.length > 1 && (
              <button type="button" className="btn btn--link" onClick={() => onChange([])}>
                {t('admin.roles.removeAllPeople')}
              </button>
            )}
          </div>
          <ul className="adm-people-list">
            {people.map((persona) => (
              <li key={persona.id} className="adm-people-item">
                <div className="adm-admin-cell">
                  <InitialsAvatar name={persona.displayName || persona.email || '?'} baseClass="rp-avatar" seed={persona.id} />
                  <div>
                    <div className="adm-admin-name">
                      {persona.displayName}
                      {rolDe(persona.id) && <span className="adm-people-tag">{t('admin.roles.alreadyAdminTag')}</span>}
                    </div>
                    <div className="adm-admin-email">{persona.email}</div>
                  </div>
                </div>
                {!isDisabled && (
                  <button
                    type="button"
                    className="btn btn--link"
                    aria-label={t('admin.roles.removePerson', { name: persona.displayName || persona.email })}
                    onClick={() => quitar(persona.id)}
                  >
                    <i className="fas fa-xmark" aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default AdminRolePeoplePicker;
